<template>
  <div class="company-info" v-if="rfObject">
    <SubHeader :title="companyName" :tabs="companyTabs" v-model:activeTab="currentTab" />
    <CompanyDetailsProfileTab
      v-if="currentTab === 'profile'"
      v-model="rfObject"
      @save="saveCompany"
      :isValidHomepage="isValidHomepage"
      :errorsFn="rfFunctions.errors" />
    <CompanyDetailsMediaTab v-if="currentTab === 'media'" v-model="companyMedia" @save="saveCompany" />
    <CompanyDetailsOrdersTab v-if="currentTab === 'orders'" :companyName="companyName" />
  </div>
</template>
<script setup lang="ts">
import CompanyDetailsProfileTab from '@/components/company/CompanyDetailsProfileTab.vue';
import CompanyDetailsMediaTab from '@/components/company/CompanyDetailsMediaTab.vue';
import CompanyDetailsOrdersTab from '@/components/company/CompanyDetailsOrdersTab.vue';
import SubHeader from '@/components/header/SubHeader.vue';
import translator from '@/locale/translator';
import { computed, ref } from 'vue';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { companyFields } from '@/autopilot/functions/companies/companyFields';
import { companyWorkflowGuide } from '@/autopilot/functions/companies/companyWorkflow';
import { buildPjasUri } from '@/func/entityLinks';
import { isValidURL } from '@/func/string/url';
import { useRemoteForm, remoteFormOptions } from '@/form/remote';
import { useToast } from 'vue-toastification';
import type { ICompany } from '@/types/company';
import type { ImageObject, OrderMedia } from '@/types/types';
import { useRoute } from 'vue-router';
import { fetchCompanyImages, updateCompanyImages } from '@/api/company';

const companyTabs = [
  {
    label: translator.get('settings.companies.profile_tab'),
    value: 'profile',
    isLoading: false,
  },
  {
    label: translator.get('settings.companies.media_tab'),
    value: 'media',
    isLoading: false,
  },
  {
    label: translator.get('nav.orders'),
    value: 'orders',
    isLoading: false,
    // badge: '2' || true, //if you want to show notification badge
  },
];

const companyId = useRoute().params.id as string;
const emit = defineEmits<{ (e: 'refetchCompanies'): void }>();

const routeReady = useAutopilotRouteReady();
useAutopilotContext('company_workflow', () => companyWorkflowGuide);

const rfOptions = remoteFormOptions('/v1/account/companies/{id}');
rfOptions.queryParameters = { resolve: 'logo_image' };
rfOptions.autopilotKey = 'current_company_form';
rfOptions.autopilotDescription = 'The company form for the company currently open in the sidebar.';
rfOptions.autopilotFields = () => companyFields;
rfOptions.autopilotSaveSensitive = true;
rfOptions.autopilotTransformer = (o) => ({
  id: o?.id,
  name: o?.name,
  display_name: o?.display_name,
  address_street: o?.address_street,
  address_city: o?.address_city,
  address_zip: o?.address_zip,
  address_country_iso: o?.address_country_iso,
  homepage: o?.homepage,
  parent_company_id: o?.parent_company_id,
  link: o?.id ? buildPjasUri('company', o.id) : null,
});

const toast = useToast();
const companyName = ref('Unknown Company');
const currentTab = ref('profile');
const companyMedia = ref<OrderMedia>({ companyLogo: [], images: [] });

rfOptions.prepareDataForSave = (data) => {
  if (companyMedia.value.companyLogo.length) data.logo_image_id = companyMedia.value.companyLogo[0].id;
  else delete data.logo_image_id;

  return data;
};
rfOptions.afterFetch = (data) => {
  if (data?.name) companyName.value = data.name;
  if (data?.id) getCompanyImages();
  routeReady();
};

const { rfObject, rfFunctions } = useRemoteForm<ICompany>(rfOptions);

/* -------------------------------- COMPUTED -------------------------------- */
const isValidHomepage = computed(() => {
  if (!rfObject.value?.homepage) return true;
  return isValidURL(rfObject.value.homepage);
});

/* --------------------------------- METHODS -------------------------------- */
async function saveCompany(stopLoading: () => void) {
  try {
    if (!rfObject.value?.id) return;

    await rfFunctions.save(stopLoading);

    await saveCompanyMedia();

    emit('refetchCompanies');
  } catch (error) {
    console.error('Error saving company:', error);
  } finally {
    stopLoading();
  }
}
async function saveCompanyMedia() {
  updateCompanyImages(
    rfObject.value!.id!,
    companyMedia.value.images.map((cm) => cm.id)
  )
    .then(() => {
      getCompanyImages();
    })
    .catch(() => toast.warning(translator.get('settings.companies.error_images_update_failed')));
}

async function getCompanyImages() {
  try {
    const newCompanyMedia: any = { companyLogo: [], images: [] };
    if (!companyId) return (companyMedia.value = newCompanyMedia);
    const response = await fetchCompanyImages(+companyId);
    response.data.forEach((media: ImageObject) => {
      if (media.type === 'company_logo') {
        newCompanyMedia.companyLogo.push(media);
      } else if (media.type === 'image') {
        newCompanyMedia.images.push(media);
      }
    });
    companyMedia.value = newCompanyMedia;
  } catch (error) {
    toast.error(translator.get('settings.companies.error_fetching_images'));
    console.error('Error fetching company images:', error);
  }
}
</script>
<style scoped>
.company-info {
  overflow-y: auto;
  padding-bottom: 3rem;
  width: 100%;
}

:deep(.company-sections-wrapper) {
  margin: 20px;
}
</style>
